// Bloco de doação Pix no rodapé do menu lateral. Renderiza QR Code SVG
// (via qrcode), o copia-e-cola Pix e a chave Pix, cada um com botão de
// copiar. Só aparece com a sidebar expandida — quando colapsada, ocupa
// muito espaço, então omitimos.
//
// Comportamento: nasce aberto, auto-recolhe após 3s com animação. Click
// na faixa colapsada reabre. Após o usuário interagir manualmente, NÃO
// auto-recolhe de novo (evita loop chato). Conectado ao espaço do menu —
// não float, sempre dentro do container do sidebar.

import { useEffect, useRef, useState } from "preact/hooks";
import { generateQrWithLogoSvg } from "@app/ui/lib/qr-with-logo";
import { Check, ChevronDown, Copy, Heart } from "lucide-react";

const PIX_COPIA_E_COLA =
  "00020126580014BR.GOV.BCB.PIX01366f2240b1-3355-429e-98f4-ba855374d45e5204000053039865802BR5925Luis Antonio Moraes Filho6009SAO PAULO62140510elgTV0ZmoZ630433CD";

const PIX_KEY = "6f2240b1-3355-429e-98f4-ba855374d45e";

export function DonationCard() {
  const [qrSvg, setQrSvg] = useState<string>("");
  const [open, setOpen] = useState<boolean>(true);
  const [userInteracted, setUserInteracted] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    generateQrWithLogoSvg(PIX_COPIA_E_COLA, {
      margin: 1,
      dark: "#0f172a",
      light: "#ffffff"
    })
      .then((svg) => {
        if (!cancelled) setQrSvg(svg);
      })
      .catch(() => {
        if (!cancelled) setQrSvg("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-collapse após 3s no primeiro mount. Só dispara enquanto o usuário
  // não interagiu — depois respeita a escolha manual dele.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!userInteracted) setOpen(false);
    }, 3000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggle() {
    setUserInteracted(true);
    setOpen((v) => !v);
  }

  return (
    <div
      class={`overflow-hidden rounded-md border border-primary/30 bg-primary/5 transition-[max-height,padding] duration-300 ease-out ${
        open ? "max-h-[440px] p-3" : "max-h-9 px-3 py-1.5"
      }`}
    >
      {/* Header — sempre clicável, faz o toggle. */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open ? "true" : "false"}
        aria-label={open ? "Recolher área de doação" : "Expandir área de doação"}
        class="flex w-full items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary hover:text-primary/80"
      >
        <Heart className="size-3.5 shrink-0" fill="currentColor" />
        <span class="flex-1 text-left">Apoie o projeto</span>
        <ChevronDown
          className={`size-3.5 shrink-0 transition-transform duration-300 ${
            open ? "" : "-rotate-90"
          }`}
        />
      </button>

      {/* Conteúdo — fade junto com a expansão pra suavizar.
          pointer-events-none quando recolhido garante que cliques
          atravessam até o header. */}
      <div
        class={`transition-opacity duration-200 ${
          open ? "mt-2 opacity-100" : "pointer-events-none opacity-0"
        }`}
        aria-hidden={open ? "false" : "true"}
      >
        <p class="mb-2.5 text-[11px] leading-relaxed text-muted-foreground">
          Doe via Pix — qualquer valor ajuda a manter o simulador no ar.
        </p>

        {qrSvg ? (
          <div
            class="mx-auto mb-2.5 size-[160px] overflow-hidden rounded-md bg-white p-1.5 shadow-sm [&_svg]:size-full"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: qrSvg }}
            aria-label="QR Code Pix"
          />
        ) : (
          <div class="mx-auto mb-2.5 grid size-[160px] place-items-center rounded-md bg-muted text-[10px] text-muted-foreground">
            Gerando QR…
          </div>
        )}

        <CopyRow label="Pix copia e cola" value={PIX_COPIA_E_COLA} truncate />
        <div class="h-2" />
        <CopyRow label="Chave Pix (Aleatória)" value={PIX_KEY} truncate />
      </div>
    </div>
  );
}

function CopyRow({
  label,
  value,
  truncate = false
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = value;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        // ignora
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div>
      <div class="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div class="flex items-center gap-1 rounded-md border border-border bg-background">
        <code
          class={`min-w-0 flex-1 px-2 py-1.5 font-mono text-[10px] leading-tight ${truncate ? "truncate" : "break-all"}`}
          title={value}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          title={copied ? "Copiado!" : `Copiar ${label.toLowerCase()}`}
          aria-label={`Copiar ${label.toLowerCase()}`}
          class={`grid size-7 shrink-0 place-items-center rounded-r-md border-l border-border transition-colors ${
            copied
              ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
              : "text-muted-foreground hover:bg-accent hover:text-foreground"
          }`}
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </div>
    </div>
  );
}
